import { isEmpty } from 'lodash-es';
import {
  forwardRef,
  type PropsWithChildren,
  type ReactElement,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { CategoricalBarChartLayout } from './CategoricalBarChartLayout.js';
import { DISPLAY_NAME } from './constants.js';
import { CategoricalBarChartBaseConfigContext } from './contexts/CategoricalBarChartBaseConfig.context.js';
import { CategoricalBarChartRawDataContext } from './contexts/CategoricalBarChartRawData.context.js';
import { useSetState } from './hooks/store/selectors.js';
import { useCategoricalBarChartConfig } from './hooks/useCategoricalBarChartConfig.js';
import { CategoricalBarChartProviders } from './providers/CategoricalBarChartProviders.js';
import { CategoricalBarDataProvider } from './providers/CategoricalBarDataProvider.js';
import { CategoricalBarFilteredDataProvider } from './providers/CategoricalBarFilteredDataProvider.js';
import { StoreProvider } from './providers/StoreProvider.js';
import { CategoryAxis } from './slots/CategoryAxis.js';
import { CategoricalBarChartDownloadCSVSlot } from './slots/DownloadCSV.js';
import { Legend } from './slots/Legend.js';
import { ThresholdIndicator } from './slots/ThresholdIndicator.js';
import { CategoricalBarChartToolbarSlot } from './slots/Toolbar.js';
import { Tooltip } from './slots/Tooltip.js';
import { ValueAxis } from './slots/ValueAxis.js';
import type {
  CategoricalBarChartProps,
  CategoricalBarChartRef,
} from './types/categorical-bar-chart.js';
import { DEFAULT_CHART_HEIGHT } from './types/defaults.js';
import { initialState } from './types/state.js';
import { buildAndDownloadCsv } from './utils/build-and-download-csv.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { useAutoLegendRefresh } from '../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { OverlayProvider } from '../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { ChartSeriesActionContext } from '../core/components/series-action/ChartSeriesActionContext.js';
import { ColorRuleContext } from '../core/context/ColorRuleContext.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import { ChartType } from '../core/types/chart-types.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';

/**
 * Component name used for data-dt-component in behavioural tracking
 */
const COMPONENT_NAME_BEHAVIOURAL_TRACKING = 'CategoricalBarChart';

/**
 * @public
 */
export const CategoricalBarChartRoot = /* @__PURE__ */ forwardRef<
  CategoricalBarChartRef,
  PropsWithChildren<CategoricalBarChartProps>
>((props, forwardedRef) => {
  const {
    data,
    height: heightProp,
    width: widthProp,
    seriesActions,
    loading = false,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;

  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);

  const chartHeight = coerceSizeValue(heightProp ?? DEFAULT_CHART_HEIGHT);
  const chartConfig = useCategoricalBarChartConfig(props);
  const { emptyState, errorState } = getChartStatesTemplates(props.children);

  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useMergeRefs<CategoricalBarChartRef | HTMLDivElement>([
    forwardedRef,
    containerRef,
  ]);

  useImperativeHandle(forwardedRef, () => ({
    element: containerRef.current,
    getConfig: () =>
      JSON.stringify({
        type: ChartType.CategoricalBar,
        ...chartConfig,
      }),
    downloadData: () => {
      buildAndDownloadCsv(data);
    },
  }));

  const baseConfig = {
    valueRepresentation: chartConfig.valueRepresentation,
    colorPalette: chartConfig.colorPalette,
    layout: chartConfig.layout,
    groupMode: chartConfig.groupMode,
    colorPaletteMode: chartConfig.colorPaletteMode,
    toolbar: chartConfig.toolbar,
    intents: chartConfig.intents,
  };

  const setState = useSetState();
  useEffect(() => {
    setState(() => initialState);
  }, [chartConfig]);
  const chartLayoutRef = useAutoLegendRefresh(data);

  return (
    <CategoricalBarChartBaseConfigContext.Provider value={baseConfig}>
      <ColorRuleContext.Provider value={chartConfig.colorRules}>
        <CategoricalBarChartRawDataContext.Provider value={data}>
          <CategoricalBarDataProvider data={data}>
            <StoreProvider>
              <ChartSeriesActionContext.Provider
                value={{
                  // We need a cast here as the provider expects unknown and the
                  // react context will not be able to carry the generic anyways.
                  chartSeriesActions: seriesActions as (
                    series: unknown,
                  ) => ReactElement<PropsWithChildren>,
                }}
              >
                <CategoricalBarChartProviders config={chartConfig}>
                  <CategoricalBarFilteredDataProvider>
                    <OverlayProvider>
                      <TooltipProvider>
                        <CategoricalBarChartLayout
                          chartLayoutRef={chartLayoutRef}
                          ref={rootRef}
                          height={chartHeight}
                          width={widthProp}
                          emptyState={emptyState}
                          errorState={errorState}
                          isEmpty={isEmpty(data)}
                          loading={loading}
                          legend={chartConfig.legend}
                          className={className}
                          style={style}
                          {...behaviorTrackingProps}
                          data-dtrum-mask={dataDtrumMask}
                          data-dtrum-allow={dataDtrumAllow}
                          data-dt-component={
                            COMPONENT_NAME_BEHAVIOURAL_TRACKING
                          }
                        />
                      </TooltipProvider>
                    </OverlayProvider>
                  </CategoricalBarFilteredDataProvider>
                </CategoricalBarChartProviders>
              </ChartSeriesActionContext.Provider>
            </StoreProvider>
          </CategoricalBarDataProvider>
        </CategoricalBarChartRawDataContext.Provider>
      </ColorRuleContext.Provider>
    </CategoricalBarChartBaseConfigContext.Provider>
  );
});

(
  CategoricalBarChartRoot as typeof CategoricalBarChartRoot & {
    displayName: string;
  }
).displayName = DISPLAY_NAME;

/**
 * The `CategoricalBarChart` visually displays the frequencies or values of
 * distinct categories using rectangular bars. Each bar's length or height
 * corresponds to the quantity or value associated with a specific category,
 * making it easy to compare the relative differences among categories.
 * @public
 */
export const CategoricalBarChart = Object.assign(CategoricalBarChartRoot, {
  Legend,
  Tooltip,
  CategoryAxis,
  ValueAxis,
  ThresholdIndicator,
  Intent,
  ColorRule,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  Toolbar: CategoricalBarChartToolbarSlot,
  DownloadCSV: CategoricalBarChartDownloadCSVSlot,
});
